/* eslint-disable react/jsx-no-undef */
import { Classes, Colors, Dialog, Intent, ButtonGroup } from "@blueprintjs/core";
import { ButtonContainer, FieldSet, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { ClubAllowedSectionsApi, ClubAllowedSectionsDto, ClubAllowedSectionsSearch } from "../../../../../api";
import { ERoutes } from "../../../../../ERoutes";
import {
  AddButton,
  CustomDataTable,
  DeleteButton,
  EditButton,
  EmailButton,
  ExportButton,
} from "../../../../../components";
import { useGlobalData } from "../../../../../contexts";
import { useApiService, useDeleteApi, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { ClubAllowedSectionsSelectorPanel } from "./ClubAllowedSectionsSelectorPanel";

export interface IClubAllowedSectionsPageProps {
  clubId: number;
  editMode: boolean;
}

const Container = styled.div`
  flex: 1;
  height: 100%;
  & > .${Classes.PANEL_STACK} {
    height: 100%;
  }
`;

export const ClubAllowedSectionsPage: React.FunctionComponent<IClubAllowedSectionsPageProps> = ({
  clubId,
  editMode,
}) => {
  const api = useApiService(ClubAllowedSectionsApi);
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();
  const [dialogOpened, setDialogOpened] = React.useState(false);
  const [sobjState, setsobjState] = React.useState<any>();
  const [clubSectionId, setclubSectionId] = React.useState(0);

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [15, 25, 50],
    pageSize: 15,
    sortKeys: { sectionName: "ASC" },
  });
  const searchFunction = React.useCallback(
    (sObj?: ClubAllowedSectionsSearch) => {
      setsobjState(
        ClubAllowedSectionsSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
          clubId: clubId,
        })
      );
      return api.baseSearch(
        ClubAllowedSectionsSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
          clubId: clubId,
        })
      );
    },
    [api, clubId, currentLanguage.languageId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });
  const { deleteItem } = useDeleteApi<number>(
    React.useMemo(
      () => ({
        deleteApiFn: (data) => api.deleteClubAllowedSection(data),
        onDeletedFn: () => search(),
      }),
      [api, search]
    )
  );

  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        hidden: !editMode,
        render: (row: ClubAllowedSectionsDto) => (
          <ButtonContainer>
            <DeleteButton
              minimal={true}
              onDelete={() => {
                deleteItem(row.clubSectionId);
              }}
            />
            <EditButton
              minimal={true}
              onClick={() => {
                setclubSectionId(row.clubSectionId);
                addItemFunction();
              }}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.Section),
        fieldName: "sectionName",
        render: (row: ClubAllowedSectionsDto) => (
          <Link target="_blank" to={`${ERoutes.section}/${row.sectionId}/detail/view`}>
            {row.sectionName}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.ContactEmails),
        fieldName: "contactEmails",
        render: (row: ClubAllowedSectionsDto) => {
          const emails = row.contactEmails?.split(";");

          return (
            <div>
              {emails?.map((email, i) => (
                <>
                  <EmailButton key={i} email={email} />
                </>
              ))}
            </div>
          );
        },
      },
    ],
    [addItemFunction, deleteItem, editMode, t]
  );

  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);

  return (
    <>
      <FieldSet
        title={t(ETLCodes.Sections)}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState} />
            {editMode && (
              <AddButton
                onClick={(e) => {
                  e.stopPropagation();
                  addItemFunction();
                }}
                text={t(ETLCodes.General_Add)}
                intent={Intent.PRIMARY}
              />
            )}
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>
      <Dialog
        title={t(ETLCodes.SectionSelection)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        className={Classes.DIALOG}
        style={{ width: 500, background: Colors.WHITE }}
      >
        <Container className={Classes.DIALOG_BODY}>
          <ClubAllowedSectionsSelectorPanel
            clubId={clubId}
            clubSectionId={clubSectionId}
            onSaved={() => {
              setclubSectionId(0);
              setDialogOpened(false);
              search();
            }}
          />
        </Container>
      </Dialog>
    </>
  );
};
